/**
 * Sanitizes a string to be used as part of a test ID.
 * Converts spaces to hyphens, removes special characters (except hyphens and underscores),
 * and converts to lowercase.
 *
 * @param value - The string to sanitize
 * @returns A sanitized string safe for use in test IDs
 */
function sanitizeTestIdPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-_]/g, ''); // Remove special characters except hyphens and underscores
}

/**
 * Generates a unique test ID by combining a base test ID with an optional identifier.
 * If no identifier is provided or if it's empty/whitespace-only, returns the base ID unchanged.
 * The identifier is sanitized to handle special characters and spaces.
 *
 * @param baseId - The base test ID constant
 * @param identifier - Optional unique identifier (e.g., field name or ID). Will be sanitized.
 * @returns A unique test ID string
 *
 * @example
 * makeUniqueTestId('select-input', 'username') // 'select-input-username'
 * makeUniqueTestId('select-input', 'user name') // 'select-input-user-name'
 * makeUniqueTestId('select-input', 'user@email') // 'select-input-useremail'
 * makeUniqueTestId('select-input', undefined) // 'select-input'
 * makeUniqueTestId('select-input', '   ') // 'select-input'
 * makeUniqueTestId('select-input', '') // 'select-input'
 */
export function makeUniqueTestId(baseId: string, identifier?: string): string {
  if (!identifier || !identifier.trim()) {
    return baseId;
  }
  const sanitized = sanitizeTestIdPart(identifier);
  return sanitized ? `${baseId}-${sanitized}` : baseId;
}

/**
 * Generates a test ID for a cancel button.
 *
 * @param baseTestId - The base test ID. If undefined, returns undefined.
 * @returns A test ID string with '-btn-cancel' suffix, or undefined if baseTestId is undefined
 *
 * @example
 * makeButtonCancelTestId('modal-delete') // 'modal-delete-btn-cancel'
 * makeButtonCancelTestId(undefined) // undefined
 */
export function makeButtonCancelTestId(baseTestId?: string): string | undefined {
  return baseTestId ? `${baseTestId}-btn-cancel` : undefined;
}

/**
 * Generates a test ID for a confirm button.
 *
 * @param baseTestId - The base test ID. If undefined, returns undefined.
 * @returns A test ID string with '-btn-confirm' suffix, or undefined if baseTestId is undefined
 *
 * @example
 * makeButtonConfirmTestId('modal-delete') // 'modal-delete-btn-confirm'
 * makeButtonConfirmTestId(undefined) // undefined
 */
export function makeButtonConfirmTestId(baseTestId?: string): string | undefined {
  return baseTestId ? `${baseTestId}-btn-confirm` : undefined;
}

/**
 * Generates a test ID for a submit button.
 *
 * @param baseTestId - The base test ID. If undefined, returns undefined.
 * @returns A test ID string with '-btn-submit' suffix, or undefined if baseTestId is undefined
 *
 * @example
 * makeButtonSubmitTestId('modal-create') // 'modal-create-btn-submit'
 * makeButtonSubmitTestId(undefined) // undefined
 */
export function makeButtonSubmitTestId(baseTestId?: string): string | undefined {
  return baseTestId ? `${baseTestId}-btn-submit` : undefined;
}
